// src/components/SessionPanel/useSession.ts
import { useCallback, useRef, useState } from "react";
import type { UpdateEvent } from "../../types/domain";
import { useApi } from "../../hooks/useApi";
import { isRealSessionId, generateLocalSessionId } from "./constants";

// Alias for internal use
const isReal = isRealSessionId;

export function useSession(opts: { onClear: () => void }) {
  const { post } = useApi();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [events, setEvents] = useState<UpdateEvent[]>([]);
  const [latestSeq, setLatestSeq] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const afterSeqRef = useRef(0);
  const seenSeqsRef = useRef<Set<number>>(new Set());
  const creatingSessionRef = useRef<Promise<string> | null>(null);

  // NEW: public helper to force a brand-new id anytime
  const mintNewSessionId = useCallback((): string => {
    const sid = generateLocalSessionId();
    setSessionId(sid);
    setEvents([]);
    seenSeqsRef.current.clear();
    afterSeqRef.current = 0;
    setLatestSeq(0);
    opts.onClear();
    return sid;
  }, [opts]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (isReal(sessionId)) return sessionId!;
    if (creatingSessionRef.current) return creatingSessionRef.current;

    const p = (async () => mintNewSessionId())();
    creatingSessionRef.current = p;
    try {
      return await p;
    } finally {
      creatingSessionRef.current = null;
    }
  }, [sessionId, mintNewSessionId]);

  const startRecording = useCallback(async () => {
    if (busy || recording) return;
    setBusy(true);
    setError(null);
    try {
      const sid = await ensureSession();
      try { await post(`/record/start?session=${sid}`); } catch {}
      setRecording(true);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [busy, recording, ensureSession, post]);

  const stopRecording = useCallback(async () => {
    if (busy || !sessionId || !recording) return;
    setBusy(true);
    setError(null);
    try {
      setRecording(false);
      // Closes the loop: summary + auto-drafted progress note, in the background.
      // Safe to repeat on stop/resume — the backend redoes them when the
      // transcript has grown.
      await post(`/sessions/${encodeURIComponent(sessionId)}/complete`);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [busy, sessionId, recording, post]);

  const stopSession = useCallback(async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      // Closes the loop: summary + auto-drafted progress note, in the background.
      await post(`/sessions/${encodeURIComponent(sessionId)}/complete`);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setRecording(false);
      setSessionId(null);
      seenSeqsRef.current.clear();
      afterSeqRef.current = 0;
      setEvents([]);
      setLatestSeq(0);
      setBusy(false);
      opts.onClear();
    }
  }, [sessionId, opts, post]);

  return {
    // state
    sessionId, setSessionId,
    recording, setRecording,
    events, setEvents,
    latestSeq, setLatestSeq,
    busy, setBusy,
    error, setError,

    // refs
    afterSeqRef, seenSeqsRef, creatingSessionRef,

    // actions
    mintNewSessionId,   // <-- expose this
    ensureSession,
    startRecording,
    stopRecording,
    stopSession,
  };
}
